<script setup lang="ts">
import { onMounted, watch } from 'vue'
import { useAppStore } from './store'
import NavBar from './components/NavBar.vue'
import SyncBar from './components/SyncBar.vue'
import InstallGate from './components/InstallGate.vue'
import BoatSetup from './components/BoatSetup.vue'
import EntryGate from './components/EntryGate.vue'
import SideMenu from './components/SideMenu.vue'
import NoticeModal from './components/NoticeModal.vue'
import PlaceWarning from './components/PlaceWarning.vue'
import { useInstallGate, isInstalled } from './lib/install'

const store = useAppStore()
const { blocking } = useInstallGate()
onMounted(() => {
  // the safe-area insets belong to the installed app; in a tab the browser's own bars
  // already hold that room, and claiming it twice leaves a gap under the tab bar
  document.documentElement.dataset['standalone'] = isInstalled() ? 'yes' : 'no'
  /**
   * Coming back to the app. `visibilitychange` is the event for it, but iOS restores a
   * standalone app from the page cache often enough that it arrives as `pageshow` instead,
   * and then nothing would ever ask again -- which is how an activated device can sit there
   * still showing the registration form. So all three are heard, and the work behind them
   * is cheap and idempotent.
   */
  const returned = () => {
    if (document.visibilityState !== 'visible' || blocking.value) return
    // also how a device notices that the link in the registration mail has been followed,
    // which usually happens in a mail programme somewhere else entirely
    void store.refreshLicence()
    void store.synchronise()
  }
  document.addEventListener('visibilitychange', returned)
  window.addEventListener('pageshow', returned)
  window.addEventListener('focus', returned)
  // measurements arrive every 10 minutes at the sources; the server refreshes every 20
  window.setInterval(() => {
    if (document.visibilityState === 'visible' && !blocking.value) void store.refreshActuals()
  }, 10 * 60_000)
  // a gated tab is a dead end: only start once the app is really in use, so no one
  // spends a phone's data on tide tables the browser is about to throw away
  watch(blocking, gated => { if (!gated) void store.init() }, { immediate: true })
})
</script>

<template>
  <InstallGate />
  <template v-if="!blocking">
    <EntryGate />
    <BoatSetup />
    <SyncBar />
    <router-view />
    <NavBar />
    <SideMenu />
    <PlaceWarning />
    <NoticeModal />
  </template>
</template>
